import React from "react";
import FemalePatient from "../assets/scanner-female.png";
import MalePatient from "../assets/scanner-male.png";
import styles from "./GenericFHIRViewerModal.module.css";

const calculateAge = (birthDate) => {
  if (!birthDate) return "";
  const dob = new Date(birthDate);
  const diff = Date.now() - dob.getTime();
  const age = new Date(diff).getUTCFullYear() - 1970;
  return age;
};

const PatientCardExtended = ({ resource }) => {
  const photoUrl =
    resource.photo?.[0]?.url ||
    (resource.gender === "female" ? FemalePatient :
     resource.gender === "male" ? MalePatient :
     null);

  const officialName = resource.name?.find(n => n.use === "official");
  const maidenName = resource.name?.find(n => n.use === "maiden");
  const address = resource.address?.[0];
  const geo = address?.extension?.find(e => e.url.includes("geolocation"))?.extension || [];
  const race = resource.extension?.find(e => e.url.includes("us-core-race"));
  const ethnicity = resource.extension?.find(e => e.url.includes("us-core-ethnicity"));
  const birthSex = resource.extension?.find(e => e.url.includes("us-core-birthsex"))?.valueCode;
  const birthPlace = resource.extension?.find(e => e.url.includes("birthPlace"))?.valueAddress;
  const mothersMaidenName = resource.extension?.find(e => e.url.includes("mothersMaidenName"))?.valueString;
  const daly = resource.extension?.find(e => e.url.includes("disability-adjusted-life-years"))?.valueDecimal;
  const qaly = resource.extension?.find(e => e.url.includes("quality-adjusted-life-years"))?.valueDecimal;

  return (
    <div className="p-4 border rounded shadow-sm bg-white">
      {/* Header with Name and Portrait */}
      <div className="d-flex align-items-start mb-4">
        <div style={{ width: "120px", height: "120px", marginRight: "1rem", flexShrink: 0 }}>
          <img
            src={photoUrl}
            alt="Patient Portrait"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "8px",
              border: "1px solid #ccc",
              backgroundColor: "#f8f9fa"
            }}
          />
        </div>
        <div>
          <h3 className="mb-1">
            {officialName?.prefix?.join(" ")} {officialName?.given?.join(" ")} {officialName?.family}
          </h3>
          <div className="mb-1 text-muted"><strong>Patient ID:</strong> {resource.id}</div>
          <div className="mb-1 text-muted"><strong>Age:</strong> {calculateAge(resource.birthDate)} ({resource.birthDate})</div>
          <div className="mb-1 text-muted"><strong>Gender:</strong> {resource.gender}</div>
          <div className="mb-1 text-muted"><strong>Birth Sex:</strong> {birthSex}</div>
          {maidenName && (
            <div className="mb-1 text-muted">
              <strong>Maiden Name:</strong> {maidenName.given?.join(" ")} {maidenName.family}
            </div>
          )}
        </div>
      </div>

      {/* Section: Identifiers */}
      <div className={styles.sectionHeader}>Identifiers</div>
      <div className="ps-5 pt-2">
        {resource.identifier?.map((id, idx) => (
          <div key={idx} className="mb-1">
            <strong>{id.type?.text || "Identifier"}:</strong> {id.value}
          </div>
        ))}
      </div>

      {/* Section: Contact */}
      <div className={styles.sectionHeader}>Contact</div>
      <div className="ps-5 pt-2">
        <div className="mb-1"><strong>Phone:</strong> {resource.telecom?.find(t => t.system === "phone")?.value}</div>
      </div>

      {/* Section: Location */}
      <div className={styles.sectionHeader}>Location</div>
      <div className="ps-5 pt-2">
        {address && (
          <>
            <div className="mb-1">
              <strong>Address:</strong>{" "}
              {address.line?.join(", ")}, {address.city}, {address.state} {address.postalCode}, {address.country}
              {geo.length > 0 && (
                <span className="text-muted">
                  {" "} (Lat: {geo.find(e => e.url === "latitude")?.valueDecimal}, Lon: {geo.find(e => e.url === "longitude")?.valueDecimal})
                </span>
              )}
            </div>
            <div className="mb-1">
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  `${address.line?.join(", ")}, ${address.city}, ${address.state} ${address.postalCode}, ${address.country}`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                View on Google Maps
              </a>
            </div>
          </>
        )}
        {birthPlace && (
          <div className="mb-1">
            <strong>Birthplace:</strong> {birthPlace.city}, {birthPlace.state}, {birthPlace.country}
          </div>
        )}
      </div>

      {/* Section: Demographics */}
      <div className={styles.sectionHeader}>Demographics</div>
      <div className="ps-5 pt-2">
        <div className="mb-1"><strong>Race:</strong> {race?.extension?.find(e => e.url === "text")?.valueString}</div>
        <div className="mb-1"><strong>Ethnicity:</strong> {ethnicity?.extension?.find(e => e.url === "text")?.valueString}</div>
        <div className="mb-1"><strong>Marital Status:</strong> {resource.maritalStatus?.text}</div>
        <div className="mb-1"><strong>Multiple Birth:</strong> {resource.multipleBirthBoolean ? "Yes" : "No"}</div>
      </div>

      {/* Section: Language */}
      <div className={styles.sectionHeader}>Language</div>
      <div className="ps-5 pt-2">
        <div className="mb-1"><strong>Preferred:</strong> {resource.communication?.[0]?.language?.text}</div>
      </div>

      {/* Section: Quality Metrics */}
      <div className={styles.sectionHeader}>Quality Metrics</div>
      <div className="ps-5 pt-2">
        <div className="mb-1"><strong>DALY:</strong> {daly}</div>
        <div className="mb-1"><strong>QALY:</strong> {qaly}</div>
      </div>
    </div>
  );
};

export default PatientCardExtended;